import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@monksflow/monks-ui';
import { FunnelIcon } from 'lucide-react';

import { ALL_REGIONS, type MarketingPerformanceFilters } from '../../model/filters';
import { AnalysisSelect } from '../../../../components';
import { formatMonth } from '../../../../utils/formatMonth';

interface DataFiltersProps {
  months: string[];
  availableRegions: string[];
  value: MarketingPerformanceFilters;
  onApply: (filters: MarketingPerformanceFilters) => void;
  onClear: () => void;
  /**
   * Bumped by the parent on every apply and every clear. `onApply` normalises an
   * inverted from/to pair, so the normalised result can equal the value already
   * applied — keying the remount on `value` alone would then skip it, leaving the
   * selects showing the inverted pair the user picked instead of what was applied.
   */
  applyCount: number;
}

// htmlFor/id is the repo idiom for pairing a <label> with a Radix trigger (see
// BriefOwnerSelect, StatusConfigModal); it also dodges a jsx-a11y nesting crash here.
const fieldClassName = 'flex flex-col gap-1';
const fieldLabelClassName = 'text-xs text-muted-foreground';

const formatMonthLabel = (month: string): string => formatMonth(month, 'numeric');

const DataFiltersInner = ({
  months,
  availableRegions,
  value,
  onApply,
  onClear,
}: DataFiltersProps) => {
  const { t } = useTranslation('analysis');
  const [pending, setPending] = useState(value);
  const fieldId = useId();

  const showRegion = availableRegions.length > 1;
  const regionLabel =
    value.region === ALL_REGIONS
      ? t('analysisResult.marketingPerformance.allRegions')
      : value.region.toUpperCase();

  const startDateLabel = t('analysisResult.marketingPerformance.startDate');
  const endDateLabel = t('analysisResult.marketingPerformance.endDate');
  const regionSelectLabel = t('analysisResult.marketingPerformance.region');

  const handleClear = () => {
    setPending(value);
    onClear();
  };

  const monthOptions = months.map((month) => ({ value: month, label: formatMonthLabel(month) }));
  const regionOptions = [
    { value: ALL_REGIONS, label: t('analysisResult.marketingPerformance.allRegions') },
    ...availableRegions.map((region) => ({ value: region, label: region.toUpperCase() })),
  ];

  return (
    <div className="flex flex-col gap-2.5 rounded-lg border border-border-light bg-surface px-4 py-3.5 dark:bg-surface-raised">
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-2">
          <FunnelIcon className="size-[15px] text-foreground" />
          <span className="text-sm font-medium text-foreground">
            {t('analysisResult.marketingPerformance.dataFilters')}
          </span>
          <span className="text-xs text-muted-foreground">
            {formatMonthLabel(value.from)} – {formatMonthLabel(value.to)} · {regionLabel}
          </span>
        </div>
        <Button variant="ghost" size="sm" className="text-primary" onClick={handleClear}>
          {t('analysisResult.marketingPerformance.clearAll')}
        </Button>
      </div>
      <div className="flex flex-wrap items-end gap-3">
        <div className={fieldClassName}>
          <label className={fieldLabelClassName} htmlFor={`${fieldId}-from`}>
            {startDateLabel}
          </label>
          <AnalysisSelect
            id={`${fieldId}-from`}
            variant="filter"
            icon="calendar"
            ariaLabel={startDateLabel}
            value={pending.from}
            onValueChange={(from) => setPending((prev) => ({ ...prev, from }))}
            options={monthOptions}
          />
        </div>
        <div className={fieldClassName}>
          <label className={fieldLabelClassName} htmlFor={`${fieldId}-to`}>
            {endDateLabel}
          </label>
          <AnalysisSelect
            id={`${fieldId}-to`}
            variant="filter"
            icon="calendar"
            ariaLabel={endDateLabel}
            value={pending.to}
            onValueChange={(to) => setPending((prev) => ({ ...prev, to }))}
            options={monthOptions}
          />
        </div>
        {showRegion && (
          <div className={fieldClassName}>
            <label className={fieldLabelClassName} htmlFor={`${fieldId}-region`}>
              {regionSelectLabel}
            </label>
            <AnalysisSelect
              id={`${fieldId}-region`}
              variant="filter"
              ariaLabel={regionSelectLabel}
              value={pending.region}
              onValueChange={(region) => setPending((prev) => ({ ...prev, region }))}
              options={regionOptions}
              className="w-[180px]"
            />
          </div>
        )}
        <div className="flex-1" />
        <Button
          // The design calls for a 40px button. Tessra's size scale changed in
          // beta.26: the old `md` is gone and `default` is now 36px, so 40px is `lg`.
          size="lg"
          onClick={() => {
            // Normalise here so the header, the charts, and the PDF all agree on the
            // order; `filterRows`' own swap stays in place as harmless belt-and-braces.
            const [from, to] =
              pending.from <= pending.to ? [pending.from, pending.to] : [pending.to, pending.from];
            onApply({ ...pending, from, to });
          }}
        >
          {t('analysisResult.marketingPerformance.applyFilters')}
        </Button>
      </div>
    </div>
  );
};

/**
 * Keying on `applyCount` forces a remount on every apply and clear, re-seeding
 * pending state without a useEffect — independent of whether the applied value
 * itself changed (see `applyCount` on `DataFiltersProps`).
 */
export const DataFilters = (props: DataFiltersProps) => (
  <DataFiltersInner
    key={`${props.applyCount}|${props.value.from}|${props.value.to}|${props.value.region}`}
    {...props}
  />
);
